import * as L from 'leaflet';

/**
 * The slice of the host this module actually needs — structural on purpose,
 * so a spec can construct it bare (`{ collection: {...} }`) exactly as
 * `voyage-map.controller.spec.ts` does for `aoiBounds`, which runs before any
 * map exists.
 */
export interface TargetHost {
  readonly map?: L.Map | null;
  collection?: any;
}

/**
 * The provider-specific answers this module must be TOLD rather than know
 * (rule R5 — no provider imports in `ais/shared/`):
 */
export interface TargetOptions {
  /**
   * Which scene on a collection is the tasked target. Voyage Radar answers
   * "the first scene with usable geometry on the first VoyageRadar sensor";
   * another provider answers from its own sensor rows. Runs against the
   * collection PASSED IN when given one — the load path calls before
   * `host.collection` is assigned.
   */
  ownScene: (collection: any) => any | null;
  /**
   * Pre-refresh POI disc radius, km — the tasked constant. Once a pull runs,
   * the served radius replaces it via the public `radiusKm` field.
   */
  defaultPoiRadiusKm: number;
  /**
   * Builds the POI reticle marker. Provider/feature-owned because the
   * nearby-vessel list hangs off it as a popup (see `bindPoiPopup`); this
   * module only places it in the target group and keeps the reference the
   * popup re-renders need.
   */
  buildReticle: (lat: number, lng: number) => any;
}

/**
 * The tasked target on the map, and the ONLY thing that draws it — extracted
 * verbatim from `VoyageMapController` (phase 2, REFACTOR-PLAN.md §7; a leaf:
 * the coupling trace showed it calls nothing in core).
 *
 * Its lifecycle is the sensor CHECKBOX, not the data. Ticked puts it up and
 * frames it; unticked takes it down; everything in between — a refresh, a
 * snapshot recall, a step through history, a shared map sweep — leaves it
 * exactly where it is.
 *
 * One layer group, whatever the shape: a yellow ring for an AOI footprint, the
 * reticle + queried-radius disc for a POI (Point) footprint. Same yellow and
 * pane as every other AOI outline in the app.
 */
export class TargetController {
  private targetLayer: any = null;
  /**
   * The POI disc, held separately from the group so a refresh can re-size it
   * in place to the radius the server actually served. Null on an AOI task.
   */
  private targetCircle: any = null;
  /**
   * The POI reticle. The nearby-vessel list hangs off it as a popup, so the
   * in-place row re-renders need it. Null on an AOI task.
   */
  private targetMarker: any = null;
  /**
   * The radius the POI disc currently draws, km — null until a POI target is
   * built or a pull reports what was served. PUBLIC AND MUTABLE for now: the
   * POI feature co-owns it (it writes the served radius and resets it with the
   * reticle) until phase 6 gives that feature its own home.
   */
  radiusKm: number | null = null;

  constructor(
    private readonly host: TargetHost,
    private readonly opts: TargetOptions,
  ) {}

  /** The POI reticle marker, or null — the popup the nearby list rides. */
  get marker(): any {
    return this.targetMarker;
  }

  /** The POI disc, or null — resized in place when a pull reports a radius. */
  get circle(): any {
    return this.targetCircle;
  }

  /**
   * Where this task is, answered off the TASK DATA — before anything is drawn.
   * The load-time framing, the zoom-to-task button and the nominatim guard all
   * read it, so it must work on a bare controller with no map attached.
   */
  aoiBounds(collection?: any): any | null {
    const scene = this.opts.ownScene(collection ?? this.host.collection);

    // A POI task's target is a point plus the radius the refresh queries — the
    // point alone would frame a zero-area view, so the disc is what "where is
    // this task" means here. The constant is the pre-refresh answer; once a
    // snapshot draws, `aoiFootprintLayer` carries the radius the server served.
    if (scene?.footprint?.type === 'Point') {
      const [lng, lat] = scene.footprint.coordinates ?? [];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      // `latLng.toBounds(diameter)`, NOT `L.circle(...).getBounds()`: Circle
      // derives its bounds by projecting through `this._map`, so an unattached
      // one throws. This runs before anything is on the map — that is the whole
      // point of it — so the bounds have to be computed, not projected.
      const bounds = L.latLng(lat, lng).toBounds(this.opts.defaultPoiRadiusKm * 2 * 1000);
      return bounds?.isValid?.() ? bounds : null;
    }

    const ring = scene?.footprint?.coordinates?.[0];
    if (!ring?.length) return null;
    // Footprints are [lng, lat] and Leaflet wants [lat, lng]; the ring has to
    // close for the polygon to bound correctly.
    const coords = ring.map((p: number[]) => [p[1], p[0]]);
    coords.push(coords[0]);
    const bounds = L.polygon(coords as any).getBounds();
    return bounds?.isValid?.() ? bounds : null;
  }

  /** Frames the task's AOI. False when there is nothing to frame. */
  showAoi(collection?: any): boolean {
    const bounds = this.aoiBounds(collection);
    if (!bounds || !this.host.map) return false;
    // On initial load the flex layout may not have settled, so Leaflet's cached
    // container size can be stale and the fit would frame a partial viewport.
    // WeatherEx calls `fixLeafletSizeCache` at the same point for the same reason.
    this.host.map.invalidateSize();
    this.host.map.fitBounds(bounds, { padding: [50, 50] });
    return true;
  }

  /**
   * Puts the tasked target on the map — the ring for an AOI task, the reticle
   * and queried-radius disc for a POI one — and optionally frames it.
   *
   * Idempotent, so every door into "the sensor is on" can call it without
   * checking first. A second call re-attaches an existing layer rather than
   * building a rival one.
   *
   * `fit` belongs to the CHECKBOX alone — the panel actions that tick it as a
   * side-effect each frame their own result instead.
   *
   * `scene` is for the one caller that already holds the geometry it means
   * (`showPoiNearby`, off the refresh it is rendering); `collection` is for
   * the load path, which runs before `host.collection` has been assigned.
   */
  showTarget(opts: { fit?: boolean; collection?: any; scene?: any } = {}): boolean {
    if (!this.host.map) return false;
    if (!this.targetLayer) {
      const scene = opts.scene ?? this.opts.ownScene(opts.collection ?? this.host.collection);
      const layer = this.buildTarget(scene);
      if (!layer) return false;
      this.targetLayer = layer;
    }
    // Both branches go through here, so a freshly built target is attached and
    // lifted by exactly the same code that re-attaches an existing one.
    this.ensureTargetOnMap();
    if (opts.fit) this.fitTarget();
    return true;
  }

  /**
   * Re-attaches the target after something swept the map out from under it.
   * `resetMapNew` removes every non-tile layer, and it runs for reasons that
   * have nothing to do with this sensor. Does not re-frame: the sweep's caller
   * owns the viewport.
   */
  ensureTargetOnMap(): void {
    if (!this.targetLayer || !this.host.map) return;
    if (!this.host.map.hasLayer(this.targetLayer)) {
      this.targetLayer.addTo(this.host.map);
    }
    // The target outlives every vessel layer drawn over it, and a snapshot that
    // lands afterwards would otherwise bury the ring under a few hundred hulls.
    // featureGroup, not layerGroup, is what makes this available.
    try {
      this.targetLayer.bringToFront?.();
    } catch (_) {}
  }

  /** Frames the drawn target. No-op when nothing is drawn. */
  fitTarget(): void {
    const bounds = this.targetBounds();
    if (bounds) this.host.map!.fitBounds(bounds, { padding: [50, 50] });
  }

  /**
   * The drawn target's bounds — what the map is actually showing, as opposed
   * to `aoiBounds`, which answers off the task data whether or not anything is
   * up. They agree on an AOI task; on a POI one this reflects the radius the
   * server served, which the constant in `aoiBounds` can only guess at before
   * a pull.
   */
  targetBounds(): any | null {
    try {
      const bounds = this.targetLayer?.getBounds?.();
      return bounds?.isValid?.() ? bounds : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Takes the target off the map. The other end of the checkbox's lifecycle —
   * and the only thing that ends it, so no data path can take the target down
   * while the box still says the sensor is on.
   */
  clearTarget(): void {
    if (this.targetLayer) (this.host.map as any)?.removeLayer?.(this.targetLayer);
    this.targetLayer = null;
    this.targetCircle = null;
    this.targetMarker = null;
  }

  /**
   * Nulls the references WITHOUT touching the map — for the task-switch reset,
   * which runs inside a shared sweep that has already taken every layer off
   * (invariant I1: a removeLayer here would operate on a map that no longer
   * holds them). The POI radius goes too: the reticle carrying it is gone.
   */
  dropRefs(): void {
    this.targetLayer = null;
    this.targetCircle = null;
    this.targetMarker = null;
    this.radiusKm = null;
  }

  /**
   * Builds the target group for a scene, or null when the scene carries no
   * usable geometry. Point footprints get the POI reticle and disc; everything
   * else gets the ring.
   */
  private buildTarget(scene: any): any | null {
    if (scene?.footprint?.type === 'Point') {
      const [lng, lat] = scene.footprint.coordinates ?? [];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      // The tasked radius is the pre-refresh answer; a POI pull re-sizes the
      // circle in place to whatever it was actually served.
      this.radiusKm = this.radiusKm ?? this.opts.defaultPoiRadiusKm;
      this.targetCircle = L.circle([lat, lng], {
        radius: this.radiusKm * 1000,
        color: 'yellow',
        fillColor: 'none',
        pane: 'left',
      });
      this.targetMarker = this.opts.buildReticle(lat, lng);
      // featureGroup, NOT layerGroup: only the former has `getBounds`, which is
      // what `fitTarget` frames the task with. (The disc contributes real bounds
      // only once the group is on the map — L.Circle projects through it — which
      // is why `showTarget` attaches before it fits.)
      return L.featureGroup([this.targetCircle, this.targetMarker]);
    }

    const ring = scene?.footprint?.coordinates?.[0];
    if (!ring?.length) return null;
    // Footprints are [lng, lat] and Leaflet wants [lat, lng].
    const coords = ring.map((p: number[]) => [p[1], p[0]]);
    const polygon = L.polygon(coords as any, {
      color: 'yellow',
      fillColor: 'none',
      pane: 'left',
      // Nothing hangs off the ring, and an interactive one would swallow clicks
      // meant for the vessels drawn inside it.
      interactive: false,
    });
    return L.featureGroup([polygon]);
  }
}
